import AddIcon from '@mui/icons-material/Add';
import { Box, Stack, Typography } from '@mui/material';
import { parse } from 'papaparse';
import { useEffect, useRef, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { toast } from 'react-toastify';

import { PrimaryButton } from 'src/components/Buttons/PrimaryButton';
import { Show } from 'src/components/Show';
import { colors } from 'src/global/styles/colors';
import { FAQ, ProjectInputs } from 'src/global/types/project.type';
import { parseConfig, validateCsv } from 'src/utils/template';

import { DownloadButton } from '../DownloadButton';
import { UploadButton } from '../UploadButton';
import { FaqItem } from './FaqItem';

interface FAQsProps {
  isDisabled?: boolean;
}

export const StrictModeDroppable = ({ children, ...props }: any) => {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));
    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);

  if (!enabled) {
    return null;
  }
  return <Droppable {...props}>{children}</Droppable>;
};

export const FAQs = ({ isDisabled }: FAQsProps) => {
  const [expandRows, setExpandRows] = useState<string[]>([]);
  const { control, trigger } = useFormContext<ProjectInputs>();
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'faqs',
  });

  const prevFieldsRef = useRef(fields);

  const [isImporting, setIsImporting] = useState(false);

  useEffect(() => {
    if (!fields.length || isImporting) return;
    const currentIds = fields.map(field => field.id);
    const previousIds = prevFieldsRef.current.map(field => field.id);

    prevFieldsRef.current = fields;

    const addedIds = currentIds.filter(id => !previousIds.includes(id));
    if (addedIds.length === 1) {
      setExpandRows(prev => [...prev, ...addedIds]);
    }

    const removedIds = previousIds.filter(id => !currentIds.includes(id));
    if (removedIds.length > 0) {
      setExpandRows(prev => prev.filter(id => !removedIds.includes(id)));
    }
  }, [fields, isImporting]);

  const handleUploadCsvFile = (files: FileList) => {
    const file = files[0];
    const reader = new FileReader();

    setIsImporting(true);

    reader.onload = async e => {
      if (!e.target?.result) return;
      const csv = e.target?.result as string;

      const validationRes = validateCsv(csv, {
        headers: ['QUESTION', 'ANSWER'],
        columnTypes: ['string', 'string'],
      });

      if (!validationRes.isValid) {
        toast.error(validationRes.errors[0]);
        return;
      }

      const parseResult = parse(csv, parseConfig);
      const lines = parseResult.data.slice(1) as [string, string][];

      // Filter out empty rows and create FAQ objects
      const faqs = lines
        .filter(line => line[0]?.trim() && line[1]?.trim())
        .map(([question, answer]) => ({
          question: question.trim(),
          answer: answer.trim(),
        }));

      remove();
      append(faqs as FAQ[]);

      setExpandRows([]);
      setIsImporting(false);

      toast.success('FAQ data imported successfully');
    };

    reader.onerror = () => {
      toast.error('Error reading the CSV file');
    };

    reader.readAsText(file);
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;
    move(result.source.index, result.destination.index);
  };

  return (
    <Box width={'100%'}>
      <Typography color='white' paddingBottom={1} fontSize={'16px'} variant='h6' lineHeight={'125%'} fontWeight={500}>
        Project's FAQ
      </Typography>
      <Show when={!isDisabled}>
        <Stack
          spacing={2}
          gap={2}
          marginTop={3}
          flexDirection={{ xs: 'column', sm: 'row' }}
          style={{
            width: 'fit-content',
          }}>
          <UploadButton
            disabled={isDisabled}
            accept='.csv'
            label='Import CSV File'
            multiple={false}
            uploadCallback={handleUploadCsvFile}
          />
          <DownloadButton
            disabled={isDisabled}
            label='Download Template'
            fileUrl='/resources/files/templates/faq-template.csv'
          />
        </Stack>
      </Show>
      <Box marginTop={3}>
        {!fields.length ? (
          <Box
            width={'100%'}
            display={'flex'}
            justifyContent={'center'}
            paddingY={2}
            sx={{
              color: 'white',
              border: `1px solid ${colors.neutral015}`,
              borderRadius: '4px',
            }}>
            No FAQs added
          </Box>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <StrictModeDroppable droppableId='faqs'>
              {(provided: any) => (
                <Box ref={provided.innerRef} {...provided.droppableProps}>
                  {fields.map((field, index) => (
                    <Draggable key={field.id} draggableId={field.id} index={index}>
                      {(provided: any) => (
                        <Box ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                          <FaqItem
                            onEdit={() => {
                              if (isDisabled) return false;
                              if (expandRows.includes(field.id)) {
                                setExpandRows(prev => prev.filter(i => i !== field.id));
                              } else {
                                setExpandRows(prev => [...prev, field.id]);
                              }
                            }}
                            isDisabled={isDisabled}
                            index={index}
                            isModifying={expandRows.includes(field.id)}
                            onDelete={() => {
                              if (isDisabled) return false;
                              remove(index);
                            }}
                            onSave={() => {
                              if (isDisabled) return false;
                              trigger([`faqs.${index}.question`, `faqs.${index}.answer`]).then(isValid => {
                                if (!isValid) return;
                                setExpandRows(prev => prev.filter(i => i !== field.id));
                              });
                            }}
                          />
                        </Box>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Box>
              )}
            </StrictModeDroppable>
          </DragDropContext>
        )}
        <Show when={!isDisabled}>
          <Box marginTop={2}>
            <PrimaryButton
              disabled={isDisabled}
              isFullWidth
              startIcon={<AddIcon />}
              onClick={() => {
                append({ question: '', answer: '' });
              }}>
              Add FAQ
            </PrimaryButton>
          </Box>
        </Show>
      </Box>
    </Box>
  );
};
